import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { InvoiceDataListItem } from 'types/invoices';

export interface InvoicesState {
    selectedDebtReminderInvoiceIds: number[];
    selectedInvoices: Record<number, InvoiceDataListItem>;
    selectedBulkSendInvoiceIds: number[];
}

const initialState: InvoicesState = {
    selectedDebtReminderInvoiceIds: [],
    selectedInvoices: {},
    selectedBulkSendInvoiceIds: [],
};

/**
 * Filters selection arrays to only include IDs that exist in the visible data.
 * This prevents non-visible items from remaining selected after filter changes.
 */
const filterNonVisibleSelections = (selectedIds: number[], visibleIds: number[]): number[] => {
    const visibleIdSet = new Set(visibleIds);
    return selectedIds.filter((id) => visibleIdSet.has(id));
};

const invoices = createSlice({
    name: 'invoices',
    initialState,
    reducers: {
        toggleDebtReminderInvoice: (state, action: PayloadAction<InvoiceDataListItem>) => {
            const invoiceId = action.payload.id;
            const index = state.selectedDebtReminderInvoiceIds.indexOf(invoiceId);
            if (index > -1) {
                state.selectedDebtReminderInvoiceIds.splice(index, 1);
                delete state.selectedInvoices[invoiceId];
            } else {
                state.selectedDebtReminderInvoiceIds.push(invoiceId);
                state.selectedInvoices[invoiceId] = action.payload;
            }
        },
        setBulkDebtReminderInvoices: (state, action: PayloadAction<InvoiceDataListItem[]>) => {
            state.selectedDebtReminderInvoiceIds = action.payload.map((inv) => inv.id);
            state.selectedInvoices = action.payload.reduce(
                (acc, invoice) => {
                    acc[invoice.id] = invoice;
                    return acc;
                },
                {} as Record<number, InvoiceDataListItem>
            );
        },
        clearDebtReminderInvoices: (state) => {
            state.selectedDebtReminderInvoiceIds = [];
            state.selectedInvoices = {};
        },
        toggleBulkSendInvoice: (state, action: PayloadAction<number>) => {
            const invoiceId = action.payload;
            const index = state.selectedBulkSendInvoiceIds.indexOf(invoiceId);
            if (index > -1) {
                state.selectedBulkSendInvoiceIds.splice(index, 1);
            } else {
                state.selectedBulkSendInvoiceIds.push(invoiceId);
            }
        },
        clearBulkSendInvoices: (state) => {
            state.selectedBulkSendInvoiceIds = [];
        },
        /**
         * Filters out selections for invoices that are not in the visible data.
         * This should be called whenever the filtered data changes to prevent
         * non-visible items from remaining selected.
         */
        filterVisibleSelections: (state, action: PayloadAction<number[]>) => {
            const visibleIds = action.payload;
            state.selectedDebtReminderInvoiceIds = filterNonVisibleSelections(
                state.selectedDebtReminderInvoiceIds,
                visibleIds
            );
            state.selectedBulkSendInvoiceIds = filterNonVisibleSelections(state.selectedBulkSendInvoiceIds, visibleIds);

            // Also filter the selectedInvoices record
            const visibleIdSet = new Set(visibleIds);
            Object.keys(state.selectedInvoices).forEach((idStr) => {
                const id = parseInt(idStr, 10);
                if (!visibleIdSet.has(id)) {
                    delete state.selectedInvoices[id];
                }
            });
        },
    },
});

export default invoices.reducer;

export const {
    toggleDebtReminderInvoice,
    setBulkDebtReminderInvoices,
    clearDebtReminderInvoices,
    toggleBulkSendInvoice,
    clearBulkSendInvoices,
    filterVisibleSelections,
} = invoices.actions;
